/* eslint-disable @typescript-eslint/ban-ts-comment */
import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";

import classname from "../../../foundation/utils/classname";
import { pipe } from "../../../foundation/utils/legacy";

import IconDragSmall from "../../v2/Icons/IconDragSmall";
import { DragSource, DropTarget, DragSourceProps } from "../../DragDropContainer";
import { NESTED_SECTION_INDENTATION } from "../specs";
import { ElevatorSectionCountableData, ElevatorSection } from "../types";

import styles from "./SectionElevatorItem.module.css";

interface SectionElevatorItemProps {
    section: ElevatorSection;
    hideItemCount?: boolean;
    reorderEnabled: boolean;
    highlight: boolean;
    shouldAdaptPosition?: boolean;
    mirror?: boolean;
    draggingSectionsData?: {
        isFirst: boolean;
        isLast: boolean;
    };
    dragMode: boolean;
    targetPosition?: string;
    dropIndicatorDepth?: number;
    highlightAsParent: boolean;
    onClick: (sectionId: string) => void;
}

interface Props extends SectionElevatorItemProps, DragSourceProps {
    index: number;
    itemHeight: number;
    allowDropToTop: boolean;
    minimumDepthLimit: number;
    maximumDepthLimit: number;
    droppable: boolean;
    setDraggingSectionId: (sectionId: string) => void;
    onStartDrag: (sectionId: string) => void;
    onSectionDrop: (args: {
        sourceId: string;
        sourceIndex: number;
        moveIndex: number;
        parent: string;
        source: string;
    }) => void;
    onParentToHighlightChange: (parent: string | null) => void;
}

interface DragDropInjectedProps {
    onStartDrag: () => void;
    onMirrorHover: ({ top, left, offsetLeft }: { top: number; left: number; offsetLeft: number; }) => void;
    onMirrorHoverOut: ({ top, left }: { top: number; left: number; }) => void;
    onDrop: (dropData: DropData) => void;
}

type DragDropProps = Props & DragDropInjectedProps;

interface DropData {
    sourceId: string;
    sourceIndex: number;
    mirrorLeft: number;
    offsetLeft: number;
}

const SECTION_ITEM_TYPES = ["projects", "screens", "components"];
const DEFAULT_SECTION_TEXT = "No Section";
const DROP_INDICATOR_LEFT_OFFSET = 24;

function getItemCount(section: ElevatorSection) {
    // TODO: Do not try to infer section item type, get `type` as prop
    const itemKey = SECTION_ITEM_TYPES.find(key => (key in section)) as ElevatorSectionCountableData;
    return itemKey ? section[itemKey]?.length : 0;
}

function SectionElevatorItem({
    section,
    hideItemCount,
    reorderEnabled,
    highlight,
    shouldAdaptPosition,
    mirror,
    draggingSectionsData,
    dragMode,
    targetPosition,
    dropIndicatorDepth,
    highlightAsParent,
    onClick
}: SectionElevatorItemProps) {
    const itemRef = useRef<HTMLDivElement>(null);
    const depth = (section.path || []).length;

    function handleClick() {
        onClick(section._id);
    }

    useEffect(() => {
        if (highlight && !shouldAdaptPosition) {
            itemRef.current.scrollIntoView();
        }
    }, [highlight, shouldAdaptPosition]);

    function renderDropIndicator() {
        if (typeof dropIndicatorDepth !== "number" || dropIndicatorDepth === -1) {
            return null;
        }

        return (
            <div
                class={styles.dropIndicator}
                style={{
                    left: DROP_INDICATOR_LEFT_OFFSET + dropIndicatorDepth * NESTED_SECTION_INDENTATION
                }}/>
        );
    }

    return (
        <div
            ref={itemRef}
            // @ts-ignore - Using dynamic classnames
            class={classname(styles.sectionElevatorItem, !!targetPosition && styles[targetPosition], "noSelect body-1", {
                [styles.mirror]: mirror,
                [styles.dragMode]: dragMode,
                [styles.dragging]: !!draggingSectionsData,
                [styles.firstDragging]: draggingSectionsData?.isFirst,
                [styles.lastDragging]: draggingSectionsData?.isLast,
                [styles.nested]: Boolean(depth),
                [styles.highlight]: highlight,
                [styles.highlightAsParent]: highlightAsParent,
                [styles.reorderEnabled]: reorderEnabled
            })}
            onClick={handleClick}>
            {renderDropIndicator()}
            <div
                class={styles.content}
                style={{ paddingLeft: (depth + 1) * NESTED_SECTION_INDENTATION }}>
                <IconDragSmall class={styles.dragHandle}/>
                <span class={classname(styles.name, "ellipsis")}>
                    {section.type === "default" ? DEFAULT_SECTION_TEXT : section.name}
                </span>
                {!hideItemCount && (
                    <span class={styles.count}>
                        {getItemCount(section)}
                    </span>
                )}
            </div>
        </div>
    );
}

const SectionElevatorItemMirror = ({ ...props }: Props) => (
    <SectionElevatorItem mirror {...props}/>
);

const RawDragDropSectionElevatorItem = pipe(
    DragSource<DragDropProps>({
        itemType: "SectionElevatorItem",
        mirrorComponent: SectionElevatorItemMirror,
        mirrorZIndex: 1000,
        onStartDrag: props => props.onStartDrag(),
        provideDragData: props => props
    }),
    DropTarget<DragDropProps>({
        acceptsItemType: "SectionElevatorItem",
        discardHorizontalPosition: true,
        onDrop: (props, dragState) => props.onDrop({
            sourceId: dragState.dragData!.section._id,
            sourceIndex: dragState.dragData!.index,
            mirrorLeft: dragState.mirrorPosition.left,
            offsetLeft: dragState.dragOffset.left
        }),
        onHover: (props, dragState) => props.onMirrorHover({
            top: dragState.mirrorPosition.top,
            left: dragState.mirrorPosition.left,
            offsetLeft: dragState.dragOffset.left
        }),
        onHoverOut: (props, dragState) => props.onMirrorHoverOut({
            top: dragState.mirrorPosition.top,
            left: dragState.mirrorPosition.left
        })
    })
)(SectionElevatorItem);

function DragDropSectionElevatorItem(props: Props) {
    const {
        index: targetIndex,
        section,
        itemHeight,
        minimumDepthLimit,
        maximumDepthLimit,
        droppable,
        allowDropToTop,
        reorderEnabled,
        setDraggingSectionId,
        onStartDrag,
        onSectionDrop,
        onParentToHighlightChange
    } = props;
    const sectionElevatorItem = useRef<{ base: HTMLDivElement; }>(null);
    const [targetPosition, setTargetPosition] = useState("");
    const [dropIndicatorDepth, setDropIndicatorDepth] = useState(-1);
    const path = section.path || [];

    function getDropParentAndDepth(
        { horizontalDiff, targetToBottom }:
        { horizontalDiff: number; targetToBottom: boolean; }
    ) {
        const draggedLevel = Math.floor(horizontalDiff / NESTED_SECTION_INDENTATION);
        const leftBoundary = Math.max(draggedLevel, minimumDepthLimit);
        const rightBoundary = Math.min(path.length + 1, maximumDepthLimit);
        const depth = targetToBottom ? Math.min(leftBoundary, rightBoundary) : 0;
        const parent = depth > path.length ? section._id : path[depth - 1];

        return { depth, parent };
    }

    function handleDragStart() {
        setDraggingSectionId(section._id);
        onStartDrag(section._id);
    }

    function handleMirrorHover({ top, left, offsetLeft }: { top: number; left: number; offsetLeft: number; }) {
        const { base } = sectionElevatorItem.current;
        const baseRect = base.getBoundingClientRect();
        const targetToBottom = !allowDropToTop || top >= baseRect.top + itemHeight / 2;

        if (!droppable && targetToBottom) {
            return;
        }

        const targetPositionText = targetToBottom ? "bottom" : "top";
        const { depth, parent } = getDropParentAndDepth({
            horizontalDiff: left - offsetLeft - baseRect.left,
            targetToBottom
        });

        onParentToHighlightChange(parent);

        if (dropIndicatorDepth !== depth) {
            setDropIndicatorDepth(depth);
        }

        if (targetPosition !== targetPositionText) {
            setTargetPosition(targetPositionText);
        }
    }

    function handleMirrorHoverOut() {
        // TODO: check mirror position and if it is past the drag&drop boundaries, persist showing the mirror

        if (targetPosition) {
            setTargetPosition("");
            setDropIndicatorDepth(-1);
            onParentToHighlightChange(null);
        }
    }

    function handleDrop({ sourceId, sourceIndex, mirrorLeft, offsetLeft }: DropData) {
        const targetToBottom = targetPosition === "bottom";

        onParentToHighlightChange(null);

        if (!targetPosition || (!droppable && targetToBottom)) {
            return;
        }

        let moveIndex = targetIndex;

        if (sourceIndex > targetIndex && targetToBottom) {
            moveIndex = targetIndex + 1;
        }

        if (sourceIndex < targetIndex && !targetToBottom) {
            moveIndex = targetIndex - 1;
        }

        const { base } = sectionElevatorItem.current;
        const baseRect = base.getBoundingClientRect();
        const { parent } = getDropParentAndDepth({
            horizontalDiff: mirrorLeft - offsetLeft - baseRect.left,
            targetToBottom
        });

        onSectionDrop({
            sourceId, sourceIndex, moveIndex, parent, source: "Section elevator"
        });
    }

    return reorderEnabled ? (
        <RawDragDropSectionElevatorItem
            {...props}
            ref={sectionElevatorItem}
            targetPosition={targetPosition}
            dropIndicatorDepth={dropIndicatorDepth}
            onStartDrag={handleDragStart}
            onMirrorHover={handleMirrorHover}
            onMirrorHoverOut={handleMirrorHoverOut}
            onDrop={handleDrop}/>
    ) : (
        <SectionElevatorItem {...props}/>
    );
}

export default SectionElevatorItem;
export { DragDropSectionElevatorItem };
